import * as React from "react";
import { cn } from "@/core/utils/utils";
import { Button, ButtonProps } from "./button";

export interface TimedButtonProps extends Omit<ButtonProps, "onClick"> {
  timeoutMs?: number;
  onTimeout: () => void;
  onClick?: () => void;
  wrapperClassName?: string;
}

const STEP_MS = 50;

const TimedButton = React.forwardRef<HTMLButtonElement, TimedButtonProps>(
  (
    {
      timeoutMs = 5000,
      onTimeout,
      onClick,
      children,
      disabled,
      className,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    const clampedTimeout = Math.max(1, timeoutMs);
    const [timeLeft, setTimeLeft] = React.useState(clampedTimeout);
    const [isActive, setIsActive] = React.useState(true);
    const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(
      null,
    );

    const progressPercentage = React.useMemo(
      () => ((clampedTimeout - timeLeft) / clampedTimeout) * 100,
      [clampedTimeout, timeLeft],
    );

    const startTimer = React.useCallback(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      setIsActive(true);
      setTimeLeft(clampedTimeout);

      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= STEP_MS) {
            setIsActive(false);
            onTimeout();
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
            return 0;
          }
          return prev - STEP_MS;
        });
      }, STEP_MS);
    }, [clampedTimeout, onTimeout]);

    React.useEffect(() => {
      startTimer();

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }, [startTimer]);

    React.useEffect(() => {
      if (timeoutMs !== timeLeft && !isActive) {
        setTimeLeft(timeoutMs);
      }
    }, [timeoutMs, timeLeft, isActive]);

    return (
      <div
        className={cn(
          "inline-flex w-auto items-center justify-center rounded-full bg-green-50",
          wrapperClassName,
        )}
      >
        <div
          className="relative overflow-hidden rounded-full"
          style={{
            background: `linear-gradient(to right, #008263 ${progressPercentage}%, transparent ${progressPercentage}%)`,
          }}
        >
          <Button
            ref={ref}
            onClick={onClick}
            disabled={disabled}
            className={cn(
              "relative z-10 text-primary-inverse-text hover:bg-green-60 hover:text-primary-inverse-text",
              className,
            )}
            {...props}
            variant="ghost"
          >
            {children}
          </Button>
        </div>
      </div>
    );
  },
);

TimedButton.displayName = "TimedButton";

export { TimedButton };
